/**
 * Draggable Block Plugin for MDXEditor
 *
 * Adds a drag handle to blocks (paragraphs, headings, etc.) allowing them to be
 * reordered by drag and drop.
 */
import { realmPlugin, addComposerChild$ } from '@mdxeditor/editor'
import { DraggableBlockPlugin_EXPERIMENTAL } from '@lexical/react/LexicalDraggableBlockPlugin'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useRef, useEffect, useState } from 'react'

const DRAGGABLE_BLOCK_MENU_CLASSNAME = 'mdx-draggable-block-menu'

function isOnMenu(element: HTMLElement): boolean {
   return !!element.closest(`.${DRAGGABLE_BLOCK_MENU_CLASSNAME}`)
}


function DraggableBlockPluginComponent() {
   const [editor] = useLexicalComposerContext()
   const menuRef = useRef<HTMLDivElement>(null)
   const targetLineRef = useRef<HTMLDivElement>(null)
   const [anchorElem, setAnchorElem] = useState<HTMLElement | null>(null)

   useEffect(() => {
      const rootElement = editor.getRootElement()
      if (rootElement) {
         const anchor = rootElement.parentElement
         if (anchor) {
            // Ensure the anchor has position relative for absolute positioning to work
            const computedStyle = window.getComputedStyle(anchor)
            if (computedStyle.position === 'static') {
               anchor.style.position = 'relative'
            }
            setAnchorElem(anchor)
         }
      }
   }, [editor])

   if (!anchorElem) {
      return null
   }

   return (
      <DraggableBlockPlugin_EXPERIMENTAL
         anchorElem={anchorElem}
         menuRef={menuRef}
         targetLineRef={targetLineRef}
         isOnMenu={isOnMenu}
         menuComponent={
            <div
               ref={menuRef}
               className={DRAGGABLE_BLOCK_MENU_CLASSNAME}
               style={{
                  position: 'absolute',
                  left: -16,
                  top: 4,
                  cursor: 'grab',
                  opacity: 0,
                  padding: '2px 1px',
                  borderRadius: '4px',
                  willChange: 'transform',
                  backgroundColor: 'transparent',
               }}
            >
               <svg width="18" height="18" viewBox="0 0 24 24" fill="#9ca3af">
                  <path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
               </svg>
            </div>
         }
         targetLineComponent={
            <div
               ref={targetLineRef}
               style={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  height: '4px',
                  background: '#3b82f6',
                  borderRadius: '2px',
                  opacity: 0,
                  pointerEvents: 'none',
               }}
            />
         }
      />
   )
}

export const draggableBlockPlugin = realmPlugin({
   init(realm) {
      realm.pub(addComposerChild$, DraggableBlockPluginComponent)
   }
})
